import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  type ReactNode,
} from 'react';
import { combine } from '@atlaskit/pragmatic-drag-and-drop/combine';
import { monitorForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { autoScrollForElements } from '@atlaskit/pragmatic-drag-and-drop-auto-scroll/element';
import { extractInstruction } from '@atlaskit/pragmatic-drag-and-drop-hitbox/tree-item';
import { announce } from '@atlaskit/pragmatic-drag-and-drop-live-region';
import { triggerPostMoveFlash } from '@atlaskit/pragmatic-drag-and-drop-flourish/trigger-post-move-flash';

import { useMoveUserStandardMutation } from '../../../standardsCatalogApi';
import type { UserStandardTreeNode } from '../../../utils/userStandardsTree';

import { isCollectionDropData, isTreeItemData } from './libraryDndData';
import {
  buildTreeLookups,
  getDescendantIds as getDescendantIdsUtil,
  instructionToMoveInput,
  type TreeLookups,
  type TreeMovePayload,
} from './treeInstructionToMove';

import type { UUID } from '@/shared/types/userStandard';

interface LibraryDndContextValue {
  lookups: TreeLookups;
  getDescendantIds: (id: UUID) => Set<UUID>;
}

const LibraryDndContext = createContext<LibraryDndContextValue | null>(null);

export function useLibraryDnd(): LibraryDndContextValue {
  const value = useContext(LibraryDndContext);
  if (!value) throw new Error('useLibraryDnd must be used within a LibraryDndProvider');
  return value;
}

interface LibraryDndProviderProps {
  ownerId: UUID;
  /** The whole library tree (not the scoped pane) so ancestor/descendant math always resolves. */
  roots: UserStandardTreeNode[];
  /** Parent of the scoped pane's top-level rows: the active collection, or null in "All collections". */
  rootParentId: UUID | null;
  scrollContainerElement: HTMLElement | null;
  onError: (message: string) => void;
  children: ReactNode;
}

export function LibraryDndProvider({
  ownerId,
  roots,
  rootParentId,
  scrollContainerElement,
  onError,
  children,
}: LibraryDndProviderProps) {
  const [moveNode] = useMoveUserStandardMutation();
  const lookups = useMemo(() => buildTreeLookups(roots), [roots]);
  const getDescendantIds = useCallback((id: UUID) => getDescendantIdsUtil(id, lookups), [lookups]);

  // The monitor registers once; it reads the latest scope/lookups through this ref so a drop in
  // flight always resolves against current data without re-subscribing mid-drag.
  const latest = useRef({
    lookups,
    rootParentId,
    ownerId,
    moveNode,
    onError,
    scrollContainerElement,
  });
  useEffect(() => {
    latest.current = { lookups, rootParentId, ownerId, moveNode, onError, scrollContainerElement };
  });

  useEffect(() => {
    return monitorForElements({
      canMonitor: ({ source }) => isTreeItemData(source.data),
      onDrop: ({ source, location }) => {
        const ctx = latest.current;
        if (!isTreeItemData(source.data)) return;
        const target = location.current.dropTargets[0];
        if (!target) return;

        const draggedId = source.data.nodeId;
        let payload: TreeMovePayload | null = null;

        if (isCollectionDropData(target.data)) {
          const newParentId = target.data.collectionId;
          const children = ctx.lookups.childrenOf(newParentId).filter((c) => c.id !== draggedId);
          const currentSiblings = ctx.lookups.childrenOf(newParentId);
          const alreadyLastChild =
            ctx.lookups.parentOf(draggedId) === newParentId &&
            currentSiblings[currentSiblings.length - 1]?.id === draggedId;
          if (!alreadyLastChild) {
            payload = {
              id: draggedId,
              newParentId,
              beforeId: children[children.length - 1]?.id ?? null,
              afterId: null,
            };
          }
        } else if (isTreeItemData(target.data)) {
          const instruction = extractInstruction(target.data);
          const targetNode = ctx.lookups.nodeMap.get(target.data.nodeId);
          if (instruction && targetNode) {
            payload = instructionToMoveInput({
              instruction,
              draggedId,
              targetNode,
              lookups: ctx.lookups,
              rootParentId: ctx.rootParentId,
            });
          }
        }

        if (!payload) return;

        const movedId = payload.id;
        const title = ctx.lookups.nodeMap.get(draggedId)?.title ?? 'item';
        void ctx
          .moveNode({
            ...payload,
            ownerId: ctx.ownerId,
            oldParentId: ctx.lookups.parentOf(draggedId),
          })
          .unwrap()
          .then(() => {
            announce(`Moved ${title}.`);
            requestAnimationFrame(() => {
              const el = ctx.scrollContainerElement?.querySelector(
                `[data-library-node-id="${movedId}"]`
              );
              if (el instanceof HTMLElement) triggerPostMoveFlash(el);
            });
          })
          .catch(() => ctx.onError('Could not move this item. Please try again.'));
      },
    });
  }, []);

  useEffect(() => {
    if (!scrollContainerElement) return;
    return combine(
      autoScrollForElements({
        element: scrollContainerElement,
        canScroll: ({ source }) => isTreeItemData(source.data),
      })
    );
  }, [scrollContainerElement]);

  const value = useMemo<LibraryDndContextValue>(
    () => ({ lookups, getDescendantIds }),
    [lookups, getDescendantIds]
  );

  return <LibraryDndContext.Provider value={value}>{children}</LibraryDndContext.Provider>;
}

export default LibraryDndProvider;
